import { Component, Input, OnChanges, OnInit, SimpleChanges, AfterViewInit, ChangeDetectorRef, ViewChildren, QueryList, ElementRef, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { KeycloakService } from 'keycloak-angular';
import { Chart } from 'chart.js/auto';
import { ParametersService, ParamType } from '@app/core/parameters';
import {
  IAcumuladosRow,
  IPatientHistory,
  PatientRequestsService,
} from '@app/core/patient-requests';
import { UsersService } from '@app/core/users';
import { MsgboxService } from '@app/shared/msgbox';
import {
  EvolutionChartsPayload,
  EvolutionChartTest,
  evolutionRequestIdsMatch,
  formatEvolutionChartAxisLabel,
  rowsToEvolutionPayload,
} from './evolution-charts-data';
import { EvolutionChartsCacheService } from './evolution-charts-cache.service';

@Component({
  selector: 'afoone-evolution-charts-view',
  templateUrl: './evolution-charts-view.component.html',
  styleUrls: ['./evolution-charts-view.component.css'],
})
export class EvolutionChartsViewComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() payload: EvolutionChartsPayload | null = null;

  @ViewChildren('chartCanvas') chartCanvases!: QueryList<ElementRef<HTMLCanvasElement>>;

  payloadFromRoute: EvolutionChartsPayload | null = null;
  loading = true;
  error: string | null = null;
  /** Mensaje del SP (msgType > 0): clave i18n bajo ACUMULADOS.* — no mostrar gráficas. */
  spMessage: string | null = null;
  limitColor = '#888';

  /** Se pone a true un tick después de tener datos; luego se crean los Chart.js en cada canvas. */
  chartReady = false;

  private charts: Chart[] = [];

  private patientId = '';
  private labNr = 0;
  private referenceDate = '';
  private requisType = 0;
  private testList = '';
  private originRequisNr = '';
  private centroId: number | null = null;

  /** Cache de opciones por test para no recrear en cada CD y que Chart.js no pierda los callbacks del tooltip. */
  private chartOptionsCache = new Map<string, unknown>();

  /** Id de la carga actual; evita que una segunda emisión de queryParams (p. ej. en ventana nueva) borre datos y llame scheduleChartReady(false). */
  private loadId = 0;

  constructor(
    private route: ActivatedRoute,
    private service: PatientRequestsService,
    private parametersService: ParametersService,
    private keycloak: KeycloakService,
    private translate: TranslateService,
    private userService: UsersService,
    private cdr: ChangeDetectorRef,
    private evolutionChartsCache: EvolutionChartsCacheService,
    private msgbox: MsgboxService
  ) {}

  get effectivePayload(): EvolutionChartsPayload | null {
    return this.payload ?? this.payloadFromRoute;
  }

  /** Paciente en formato IPatientHistory para afoone-patient-data. */
  get patientForDisplay(): IPatientHistory | null {
    const p = this.effectivePayload?.patient;
    if (!p) return null;
    return {
      apellidos: p.lastName,
      nombre: p.name,
      historia: p.id,
      sexo: p.sex,
      edad: p.age,
      fechaNac: p.birthDate,
    } as IPatientHistory;
  }

  ngOnInit(): void {
    this.parametersService
      .getParameters([ParamType.ColorPatologico])
      .subscribe((p) => {
        if (p?.length) {
          this.limitColor = p[0].value ?? this.limitColor;
        }
      });

    this.route.queryParams.subscribe(async (qp) => {
      if (this.payload != null) {
        this.loading = false;
        return;
      }
      this.patientId = qp['patientId'] ?? '';
      this.labNr = Number.parseInt(qp['labNr'] ?? '0', 10);
      this.referenceDate = qp['referenceDate'] ?? '';
      this.requisType = Number.parseInt(qp['requisType'] ?? '0', 10);
      this.testList = qp['testList'] ?? '';
      const nextOriginRequisNr = String(qp['originRequisNr'] ?? '');
      if (nextOriginRequisNr !== this.originRequisNr) {
        this.originRequisNr = nextOriginRequisNr;
        this.chartOptionsCache.clear();
      }
      const centroIdParam = qp['centroId'];
      if (centroIdParam != null && centroIdParam !== '') {
        const c = Number(centroIdParam);
        if (!Number.isNaN(c)) {
          this.centroId = c;
          this.userService.setSisifoDefaultCenter(c);
        } else {
          this.centroId = null;
        }
      } else {
        this.centroId = this.userService.getSisifoDefaultCenter();
      }
      if (!this.patientId) {
        this.loading = false;
        this.error = 'Faltan parámetros (patientId).';
        return;
      }
      const cacheKey = EvolutionChartsCacheService.cacheKey({
        patientId: this.patientId,
        labNr: this.labNr,
        referenceDate: this.referenceDate,
        requisType: this.requisType,
        testList: this.testList,
        centroId: this.centroId,
      });
      const cached = this.evolutionChartsCache.get(cacheKey);
      if (cached?.tests?.length) {
        this.payloadFromRoute = cached;
        this.loading = false;
        this.error = null;
        this.scheduleChartReady();
      }
      const hasToken = await this.waitForToken();
      if (!hasToken) {
        this.loading = false;
        this.error =
          'Sesión no disponible. Cierra esta pestaña e intenta de nuevo desde la pestaña original.';
        return;
      }
      this.loadData();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['payload'] && this.payload != null) {
      this.loading = false;
      this.error = null;
    }
    if (changes['payload'] || changes['payloadFromRoute']) {
      this.chartOptionsCache.clear();
    }
    this.scheduleChartReady();
  }

  ngAfterViewInit(): void {
    this.scheduleChartReady();
  }

  ngOnDestroy(): void {
    this.destroyCharts();
  }

  private destroyCharts(): void {
    this.charts.forEach((c) => {
      try {
        c.destroy();
      } catch (_) {}
    });
    this.charts = [];
  }

  private createCharts(): void {
    this.destroyCharts();
    const tests = this.effectivePayload?.tests ?? [];
    const canvases = this.chartCanvases?.toArray() ?? [];
    if (canvases.length !== tests.length) return;
    tests.forEach((test, i) => {
      const el = canvases[i]?.nativeElement;
      if (!el) return;
      const data = this.getChartData(test);
      const options = this.getChartOptions(test);
      const plugins = this.getChartPlugins(test);
      const chart = new Chart(el, { type: 'line', data, options, plugins });
      this.charts.push(chart);
    });
  }

  /** Activa chartReady un tick después si hay datos; luego crea los Chart.js en cada canvas. */
  private scheduleChartReady(): void {
    const hasData = this.effectivePayload?.tests?.length;
    if (!hasData) {
      this.destroyCharts();
      this.chartReady = false;
      return;
    }
    if (this.chartReady) return;
    const setReady = (): void => {
      this.chartReady = true;
      this.cdr.detectChanges();
      setTimeout(() => this.createCharts(), 0);
    };
    requestAnimationFrame(() => {
      requestAnimationFrame(setReady);
    });
  }

  private async waitForToken(maxWaitMs = 5000): Promise<boolean> {
    const deadline = Date.now() + maxWaitMs;
    while (Date.now() < deadline) {
      const token = await this.keycloak.getToken();
      if (token) return true;
      await new Promise((r) => setTimeout(r, 150));
    }
    return false;
  }

  private loadData(): void {
    const myLoadId = ++this.loadId;
    const loadCacheKey = EvolutionChartsCacheService.cacheKey({
      patientId: this.patientId,
      labNr: this.labNr,
      referenceDate: this.referenceDate,
      requisType: this.requisType,
      testList: this.testList,
      centroId: this.centroId,
    });
    const hadCached = this.evolutionChartsCache.get(loadCacheKey)?.tests?.length;
    if (!hadCached) {
      this.loading = true;
      this.destroyCharts();
      this.chartReady = false;
      this.error = null;
      this.spMessage = null;
      this.payloadFromRoute = null;
      this.chartOptionsCache.clear();
    }
    Promise.all([this.loadPatient(), this.loadAcumulados()])
      .then(([patient, acRes]) => {
        if (myLoadId !== this.loadId) return;
        if (!patient) {
          this.error = 'Paciente no encontrado.';
          this.loading = false;
          return;
        }
        if (Number(acRes.msgType) > 0 && acRes.msgCode?.trim()) {
          this.spMessage = acRes.msgCode.trim();
          this.payloadFromRoute = null;
          this.destroyCharts();
          this.chartReady = false;
          this.loading = false;
          return;
        }
        const rows = acRes.data;
        if (!rows?.length) {
          this.error = 'Sin datos acumulados para gráficas.';
          this.loading = false;
          return;
        }
        this.payloadFromRoute = rowsToEvolutionPayload(rows as IAcumuladosRow[], patient);
        if (this.payloadFromRoute?.tests?.length) {
          const key = EvolutionChartsCacheService.cacheKey({
            patientId: this.patientId,
            labNr: this.labNr,
            referenceDate: this.referenceDate,
            requisType: this.requisType,
            testList: this.testList,
            centroId: this.centroId,
          });
          this.evolutionChartsCache.set(key, this.payloadFromRoute);
        }
        this.cdr.detectChanges();
        setTimeout(() => this.createCharts(), 0);
      })
      .catch((err) => {
        if (myLoadId !== this.loadId) return;
        this.error = err?.message || err?.error?.message || 'Error al cargar datos.';
      })
      .finally(() => {
        this.loading = false;
        if (myLoadId !== this.loadId) return;
        this.scheduleChartReady();
      });
  }

  private loadPatient(): Promise<IPatientHistory | null> {
    return new Promise((resolve) => {
      this.service
        .getPatientHistories({
          history: this.patientId.trim(),
          searchType: 'C',
          centroId: this.centroId ?? undefined,
        } as any)
        .subscribe({
          next: (list) => {
            const normalizedId = this.patientId.trim().replace(/^\*/, '');
            const p = list?.find((x) => {
              const h = (x.historia?.trim() ?? '').replace(/^\*/, '');
              return h === normalizedId || (x.historia?.trim() ?? '') === this.patientId.trim();
            });
            resolve(p ?? null);
          },
          error: (err) => {
            const key = err?.error?.message || err?.message;
            if (key && typeof key === 'string' && key.startsWith('WEB_RESULTS.ERROR.')) {
              this.msgbox.showError(key);
            } else {
              this.msgbox.showError('ERRORS.INTERNAL_SERVER_ERROR');
            }
            resolve(null);
          },
        });
    });
  }

  private loadAcumulados(): Promise<{
    data: IAcumuladosRow[];
    msgCode?: string;
    msgType?: number;
  }> {
    return new Promise((resolve, reject) => {
      this.service
        .searchAcumulados({
          patientId: this.patientId,
          labNr: this.labNr,
          referenceDate: this.referenceDate,
          requisType: this.requisType,
          testList: this.testList || undefined,
          graphicFormat: 1,
          centroId: this.centroId ?? undefined,
        })
        .subscribe({
          next: (res) => {
            const data = Array.isArray(res) ? res : (res as { data?: unknown[] })?.data ?? [];
            const msgCode = (res as { msgCode?: string })?.msgCode;
            const msgType = (res as { msgType?: number })?.msgType;
            resolve({
              data: (data as IAcumuladosRow[]) || [],
              msgCode: msgCode?.trim() || undefined,
              msgType: msgType != null ? Number(msgType) : 0,
            });
          },
          error: (err) => reject(err),
        });
    });
  }

  getChartData(test: EvolutionChartTest): { labels: string[]; datasets: any[] } {
    const labels = test.points.map((pt) =>
      formatEvolutionChartAxisLabel(pt.requestId, pt.dateTime, pt.externalId)
    );
    const upperData = test.points.map((pt) => pt.upperLimit);
    const lowerData = test.points.map((pt) => pt.lowerLimit);
    const resultData = test.points.map((pt) => pt.result);
    const refLimitsLabel = this.translate.instant('EVOLUTION_CHARTS.REFERENCE_LIMITS');
    const resultLabel = this.translate.instant('EVOLUTION_CHARTS.RESULT');
    return {
      labels,
      datasets: [
        {
          label: refLimitsLabel,
          data: upperData,
          borderColor: this.limitColor,
          backgroundColor: 'transparent',
          borderWidth: 2,
          borderDash: [6, 4],
          fill: false,
          pointRadius: 4,
          pointHoverRadius: 8,
        },
        {
          label: refLimitsLabel,
          data: lowerData,
          borderColor: this.limitColor,
          backgroundColor: 'transparent',
          borderWidth: 2,
          borderDash: [6, 4],
          fill: false,
          pointRadius: 4,
          pointHoverRadius: 8,
        },
        {
          label: resultLabel,
          data: resultData,
          borderColor: '#1976d2',
          backgroundColor: 'transparent',
          borderWidth: 2,
          fill: false,
          pointRadius: 4,
          pointHoverRadius: 8,
        },
      ],
    };
  }

  getChartOptions(test: EvolutionChartTest): any {
    const cacheKey = `${test.testCode}-${test.testName}-${this.originRequisNr}`;
    const cached = this.chartOptionsCache.get(cacheKey) as any;
    if (cached) return cached;

    const unit = test.unit ? ` ${test.unit}` : '';
    const upperLabel = this.translate.instant('EVOLUTION_CHARTS.UPPER_LIMIT');
    const lowerLabel = this.translate.instant('EVOLUTION_CHARTS.LOWER_LIMIT');
    const resultLabel = this.translate.instant('EVOLUTION_CHARTS.RESULT');
    const points = test.points;
    const options = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index',
        intersect: false,
      },
      plugins: {
        legend: {
          display: true,
          labels: {
            font: { size: 13 },
            padding: 14,
            pointStyle: 'line',
            usePointStyle: true,
            filter: (_item: { datasetIndex: number }, _chart: unknown) => {
              return _item.datasetIndex !== 1;
            },
          },
        },
        tooltip: {
          enabled: true,
          mode: 'index',
          intersect: false,
          callbacks: {
            title: (items: { dataIndex?: number }[]) => {
              if (items?.length && items[0].dataIndex != null && points[items[0].dataIndex]) {
                const pt = points[items[0].dataIndex];
                return formatEvolutionChartAxisLabel(
                  pt.requestId,
                  pt.dateTime,
                  pt.externalId
                ).replace(/\n/g, ' ');
              }
              return '';
            },
            label: (context: { dataIndex: number; datasetIndex: number; parsed: { y?: number | null }; dataset?: { label?: string } }) => {
              const i = context.dataIndex;
              const pt = points[i];
              if (!pt) return (context.dataset?.label ?? '') + ': ' + (context.parsed?.y ?? '');
              if (context.datasetIndex === 0) return `${upperLabel}: ${pt.upperLimit ?? ''}${unit}`;
              if (context.datasetIndex === 1) return `${lowerLabel}: ${pt.lowerLimit ?? ''}${unit}`;
              const range = pt.normalRange ? ` (${pt.normalRange})` : '';
              return `${resultLabel}: ${pt.result ?? ''}${unit}${range}`;
            },
          },
        },
      },
      scales: {
        x: {
          ticks: {
            maxRotation: 45,
            minRotation: 45,
            font: (context: { tick?: { value?: number | string } }) => {
              const index = Number(context.tick?.value);
              return {
                size: 11,
                weight: this.isOriginPoint(test, index) ? 'bold' : 'normal',
              };
            },
          },
        },
        y: { beginAtZero: false },
      },
    };
    this.chartOptionsCache.set(cacheKey, options);
    return options;
  }

  getChartPlugins(test: EvolutionChartTest): any[] {
    return [
      {
        id: `origin-request-marker-${test.testCode}`,
        afterDatasetsDraw: (chart: any) => {
          const originIndex = this.findOriginPointIndex(test);
          if (originIndex < 0) return;
          const xScale = chart.scales?.x;
          const chartArea = chart.chartArea;
          const ctx = chart.ctx;
          if (!xScale || !chartArea || !ctx) return;

          const x = xScale.getPixelForValue(originIndex);
          ctx.save();
          ctx.beginPath();
          ctx.setLineDash?.([5, 4]);
          ctx.lineWidth = 2;
          ctx.strokeStyle = '#c45500';
          ctx.moveTo(x, chartArea.top);
          ctx.lineTo(x, chartArea.bottom);
          ctx.stroke();
          ctx.restore();
        },
      },
    ];
  }

  private findOriginPointIndex(test: EvolutionChartTest): number {
    return test.points.findIndex((_point, index) => this.isOriginPoint(test, index));
  }

  private isOriginPoint(test: EvolutionChartTest, index: number): boolean {
    if (!Number.isInteger(index)) return false;
    const point = test.points[index];
    if (!point) return false;
    return (
      evolutionRequestIdsMatch(point.requestId, this.originRequisNr) ||
      evolutionRequestIdsMatch(point.externalId, this.originRequisNr)
    );
  }
}
